import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { format, parseISO } from 'date-fns';
import { unwrapResult } from '@reduxjs/toolkit';
import { useDispatch, useSelector } from 'react-redux';
import { useDocumentTitle, usePageTitleHeader } from '@clatter/ui';
import Page from '../../../components/Page/Page';
import MicrositesDetailFilters, {
  DEFAULT_SORT_MODEL,
  DISPLAY_TYPE_PAGE,
  DISPLAY_TYPE_SITE,
} from './Filters/MicrositesDetailFilters';
import MicrositesDetailPageTable, {
  getColumns as pageTableColumns,
} from './MicrositesDetailPageTable';
import {
  fetchReport,
  platformSelectors,
  reportTypes,
  requestStatus,
  useNotices,
  useRoutesState,
} from '@clatter/platform';
import routes, { documentTitleMap } from '../../../routes/routes';
import { micrositesDetailFilter } from './Filters/micrositesDetailFilter';
import MicrositesDetailSiteTable, {
  getColumns as siteTableColumns,
} from './MicrositesDetailSiteTable';
import { FormProvider, useForm } from 'react-hook-form';
import { PAGE_VIEWS_FILTER_KEY } from "../Pageviews/Filters/PageViewsFilters";

const StyledTableWrapper = styled.div`
  margin-top: 20px;

  .even {
    background-color: #ffffff;
  }

  .odd {
    background-color: #f7f7f7;
  }
`;

const StyledPageContent = styled.div`
  min-width: 300px;
  max-width: calc(100vw - 80px);
  margin: 0 auto;
  padding: 32px 16px;
  min-height: calc(100vh - 64px);
`;

const MicrositesDetailView = () => {
  const { documentFileName } = useDocumentTitle(
    documentTitleMap.reportsMicrositesDetail.document,
  );
  const { getRouteStateProperty } = useRoutesState();
  const dispatch = useDispatch();
  const { addNotice } = useNotices();
  const [_, setFilterValues] = useState();
  const [rows, setRows] = useState([]);

  const persistedFormState = getRouteStateProperty(PAGE_VIEWS_FILTER_KEY);

  const isLoading =
    useSelector(platformSelectors.report.loadingStatus) ===
    requestStatus.pending;

  const reportData = useSelector(
    platformSelectors.report.selectReportByName(
      reportTypes.msmMicrositesDetail,
    ),
  );

  const { renderPageTitleHeader } = usePageTitleHeader({
    pageTitle: documentTitleMap.reportsMicrositesDetail.page,
    routes: routes,
  });

  const formMethods = useForm({
    mode: 'onChange',
    defaultValues: {
      date: {},
      search_query: persistedFormState?.search_query || '',
      display_type: persistedFormState?.display_type || DISPLAY_TYPE_PAGE,
      sort_model: persistedFormState?.sort_model || DEFAULT_SORT_MODEL,
    },
  });

  const { setValue, getValues, watch } = formMethods;

  const dateValue = watch('date');
  const displayTypeValue = watch('display_type');
  const sortModelValue = watch('sort_model');
  const searchQuery = watch('search_query');

  const exportFileName = useMemo(() => {
    if (displayTypeValue === DISPLAY_TYPE_PAGE) {
      return `${documentFileName} (Page)`;
    }
    if (displayTypeValue === DISPLAY_TYPE_SITE) {
      return `${documentFileName} (Site)`;
    }
    return documentFileName;
  }, [documentFileName, displayTypeValue]);

  useEffect(() => {
    if (!dateValue.hasOwnProperty('dateRange') || isLoading) {
      return;
    }

    (async () => {
      try {
        const resultAction = await dispatch(
          fetchReport({
            reportName: reportTypes.msmMicrositesDetail,
            params: {
              startDate: format(
                parseISO(dateValue?.dateRange.range.startDate),
                'yyyy-MM-dd',
              ),
              endDate: format(
                parseISO(dateValue?.dateRange.range.endDate),
                'yyyy-MM-dd',
              ),
            },
          }),
        );
        unwrapResult(resultAction); // needs to have this in order to catch errors
      } catch (error) {
        setRows([]);
        addNotice({
          message: [
            'Error during fetching data from the API.',
            error?.message && `Reason: ${error.message}`,
          ].join(' '),
          type: 'error',
          title: 'Error',
        });
      }
    })();
  }, [
    dispatch,
    dateValue?.dateRange?.range?.startDate,
    dateValue?.dateRange?.range?.endDate,
  ]);

  // set reports data on filter change
  useEffect(() => {
    if (reportData?.data?.length === 0) {
      setRows([]);
    }

    if (reportData?.data?.length && dateValue.hasOwnProperty('dateRange')) {
      setRows(
        micrositesDetailFilter({
          data: reportData?.data,
          filters: getValues(),
          tableColumns:
            displayTypeValue === DISPLAY_TYPE_PAGE
              ? pageTableColumns({ extraColumns: [] })
              : siteTableColumns({ extraColumns: [] }),
        }),
      );
    }
  }, [
    reportData?.data,
    dateValue?.dateRange?.range.startDate,
    dateValue?.dateRange?.range.endDate,
    searchQuery,
    displayTypeValue,
    sortModelValue?.field,
    sortModelValue?.sort,
  ]);

  return (
    <Page withPageContent={false}>
      <FormProvider {...formMethods}>
        <StyledPageContent>

          { renderPageTitleHeader() }

          <MicrositesDetailFilters
            disabled={isLoading}
            onFiltersChange={setFilterValues}
          />
          <StyledTableWrapper>
            {displayTypeValue === DISPLAY_TYPE_PAGE && (
              <MicrositesDetailPageTable
                isLoading={isLoading}
                rows={rows}
                name="report/msm-microsites-detail-page"
                exportFileName={exportFileName}
              />
            )}

            {displayTypeValue === DISPLAY_TYPE_SITE && (
              <MicrositesDetailSiteTable
                isLoading={isLoading}
                rows={rows}
                name="report/msm-microsites-detail-site"
                exportFileName={exportFileName}
              />
            )}
          </StyledTableWrapper>
        </StyledPageContent>
      </FormProvider>
    </Page>
  );
};

export default MicrositesDetailView;
